import { defineStore } from 'pinia'
import { useRequest } from 'composables/useRequest.js'
import { useResumesStore } from 'stores/resumes.js'
import { SAMPLE_SECTIONS, sampleSections } from 'components/resumes/editor/sampleContent.js'

const SAVE_DELAY = 1200 // ms of quiet before a change is saved

let timer = null

// The resume open in the editor. `content` is edited in place by the forms, and saved a moment
// after the user stops typing. Saving is one request at a time: a change made while one is in
// flight is sent right after it, so the last edit always wins.
export const useResumeEditorStore = defineStore('resumeEditor', {
  state: () => ({
    resume: null, // id, name, template, is_default, ...
    name: '',
    content: null,
    loading: false,
    error: null, // why it could not be loaded
    notFound: false,

    saveState: 'saved', // saved | dirty | saving | error
    saveError: null,
    resaveAfter: false, // changed while a save was in flight
    beforeSample: null, // what the built in sections held before sample data replaced them
  }),

  getters: {
    unsaved: (state) => state.saveState !== 'saved',
  },

  actions: {
    async load(id) {
      clearTimeout(timer)
      Object.assign(this, { loading: true, error: null, notFound: false, resume: null, content: null, saveState: 'saved', saveError: null, resaveAfter: false, beforeSample: null })

      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('GET', `/api/v1/resumes/${id}`, {}, false)
      } catch {
        res = { js_error: true }
      }

      if (!res.js_error && res.response?.success) {
        const { content, ...resume } = res.response.data
        this.resume = resume
        this.name = resume.name
        this.content = content
      } else if (res.response?.status === 404 || res.response?.message === 'Resume not found.') {
        this.notFound = true
      } else {
        this.error = res.response?.message || 'Failed to load this resume.'
      }
      this.loading = false
    },

    // Called after every edit. The save waits for a quiet moment.
    touch() {
      if (!this.resume) return
      this.saveState = this.saveState === 'saving' ? 'saving' : 'dirty'
      this.resaveAfter = this.resaveAfter || this.saveState === 'saving'
      clearTimeout(timer)
      timer = setTimeout(() => this.save(), SAVE_DELAY)
    },

    setName(name) {
      this.name = name
      this.touch()
    },

    setTemplate(template) {
      this.resume.template = template
      this.touch()
    },

    // Saves now (the editor is being left, or the user pressed retry).
    async flush() {
      clearTimeout(timer)
      if (this.saveState === 'dirty' || this.saveState === 'error') await this.save()
    },

    async save() {
      clearTimeout(timer)
      if (!this.resume) return
      if (this.saveState === 'saving') {
        this.resaveAfter = true
        return
      }

      this.saveState = 'saving'
      this.resaveAfter = false
      this.saveError = null
      const { axiosRequest } = useRequest()
      let res
      try {
        res = await axiosRequest('PATCH', `/api/v1/resumes/${this.resume.id}`,
          { resume_title: this.name.trim() || this.resume.name, template: this.resume.template, content: this.content }, false)
      } catch {
        res = { js_error: true }
      }

      if (!res.js_error && res.response?.success) {
        this.resume.updated_at = res.response.data.updated_at
        this.resume.name = res.response.data.name
        this.saveState = 'saved'
        useResumesStore().invalidate() // the list shows the name, template and last edit
      } else {
        this.saveState = 'error'
        this.saveError = res.response?.message || 'Could not save. Check your connection.'
        return
      }

      if (this.resaveAfter) {
        this.saveState = 'dirty'
        await this.save()
      }
    },

    // Whether there is anything of the user's in the built in sections, so replacing it needs asking.
    hasOwnContent() {
      const { basics, summary } = this.content
      return Boolean(basics.full_name.trim() || summary.trim() || SAMPLE_SECTIONS.slice(2).some((key) => this.content[key].length))
    },

    // Fills the built in sections with example content, to show what a full resume looks like.
    // What was there is kept for `undoSample`. The look, the picture and custom sections stay.
    fillSample() {
      this.beforeSample = JSON.parse(JSON.stringify(Object.fromEntries(SAMPLE_SECTIONS.map((key) => [key, this.content[key]]))))
      Object.assign(this.content, sampleSections())
      this.touch()
    },

    undoSample() {
      if (!this.beforeSample) return
      Object.assign(this.content, this.beforeSample)
      this.beforeSample = null
      this.touch()
    },

    dismissSample() {
      this.beforeSample = null
    },

    // The picture is saved by its own upload, not with the document, so this only follows it.
    setPicture(url) {
      this.resume.picture_url = url || null
      useResumesStore().invalidate()
    },

    // Custom sections also have a place in the section order, which the preview follows.
    addCustomSection(title) {
      const id = newCustomId()
      this.content.custom.push({ id, title, entries: [] })
      this.content.settings.section_order.push(id)
      this.touch()
      return id
    },

    removeCustomSection(id) {
      this.content.custom = this.content.custom.filter((section) => section.id !== id)
      this.content.settings.section_order = this.content.settings.section_order.filter((key) => key !== id)
      this.content.settings.hidden_sections = this.content.settings.hidden_sections.filter((key) => key !== id)
      this.touch()
    },
  },
})

const newCustomId = () => `custom-${Math.random().toString(36).slice(2, 10)}`
